import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  inject,
  PLATFORM_ID,
  signal,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { FooterComponent } from './components/footer/footer.component';
import { HeaderComponent } from './components/header/header.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, Subject, tap, throwError } from 'rxjs';
import { RewardsSectionComponent } from './components/rewards-section/rewards-section.component';
import { WhatTodoSectionComponent } from './components/what-todo-section/what-todo-section.component';
import { TopSectionComponent } from './components/top-section/top-section.component';
import { VideoSectionComponent } from './components/video-section/video-section.component';
import { FollowSectionComponent } from './components/follow-section/follow-section.component';
import { FaqSectionComponent } from './components/faq-section/faq-section.component';
import { ScrollToTopComponent } from './components/scroll-to-top/scroll-to-top.component';
import { ProductsSectionComponent } from './components/products-section/products-section.component';
import { WinnersSectionComponent } from './components/winners-section/winners-section.component';
import { ApiService } from '@app/services/api.service';
import { SectionType } from '@app/models/section-type';
import { isPlatformServer } from '@angular/common';
import { GoogleTagManagerService } from '@app/services/google-tag-manager.service';
import { debounceTime } from 'rxjs/operators';
import { Router } from '@angular/router';
import { FormSectionComponent } from '@app/pages/main-page/components/form-section/form-section.component';
import { ApplicationFormModel, ApplicationSaveDto } from '@app/models';
import { FormControl, FormGroup } from '@angular/forms';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { environment } from '../../../environments/environment';
import { v4 as uuidv4 } from 'uuid';
import { CounterSectionComponent } from '@app/pages/main-page/components/counter-section/counter-section.component';
import { BlikSectionComponent } from '@app/pages/main-page/components/blik-section/blik-section.component';
import { NgxSkeletonLoaderComponent } from 'ngx-skeleton-loader';
import { toZonedTime } from 'date-fns-tz';
import { EndTopSectionComponent } from '@app/pages/main-page/components/end-top-section/end-top-section.component';

@Component({
  standalone: true,
  imports: [
    FooterComponent,
    HeaderComponent,
    RewardsSectionComponent,
    WhatTodoSectionComponent,
    TopSectionComponent,
    VideoSectionComponent,
    FollowSectionComponent,
    FaqSectionComponent,
    ScrollToTopComponent,
    ProductsSectionComponent,
    WinnersSectionComponent,
    FormSectionComponent,
    CounterSectionComponent,
    BlikSectionComponent,
    NgxSkeletonLoaderComponent,
    EndTopSectionComponent,
  ],
  templateUrl: './main-page.component.html',
  encapsulation: ViewEncapsulation.None,
})
export class MainPageComponent implements AfterViewInit {
  @ViewChild('topSection', { static: false, read: ElementRef })
  public topSection?: ElementRef;
  @ViewChild('rewardsSection', { static: false, read: ElementRef })
  public rewardsSection?: ElementRef;
  @ViewChild('videoSection', { static: false, read: ElementRef })
  public videoSection?: ElementRef;
  @ViewChild('whatTodoSection', { static: false, read: ElementRef })
  public whatTodoSection?: ElementRef;
  @ViewChild('formSection', { static: false, read: ElementRef })
  public formSection?: ElementRef;
  @ViewChild('blikSection', { static: false, read: ElementRef })
  public blikSection?: ElementRef;
  @ViewChild('productsSection', { static: false, read: ElementRef })
  public productsSection?: ElementRef;
  @ViewChild('winnersSection', { static: false, read: ElementRef })
  public winnersSection?: ElementRef;
  @ViewChild('followSection', { static: false, read: ElementRef })
  public followSection?: ElementRef;
  @ViewChild('faqSection', { static: false, read: ElementRef })
  public faqSection?: ElementRef;
  public readonly endDate: Date = new Date('2025-11-15T23:59:59Z');
  private readonly _apiService = inject(ApiService);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _googleTagManagerService = inject(GoogleTagManagerService);
  private readonly _platformId = inject(PLATFORM_ID);
  private readonly _router = inject(Router);
  private readonly _ngxSpinnerService = inject(NgxSpinnerService);
  private readonly _toastrService = inject(ToastrService);
  private readonly _changeDetectorRef = inject(ChangeDetectorRef);
  private readonly _scrollSubject = new Subject<void>();
  private readonly _scroll$ = this._scrollSubject.asObservable();

  public readonly lotteryEnded = signal(false);

  constructor() {
    const currentDate = new Date();
    const timeZone = 'Europe/Warsaw';
    const zoneDate = toZonedTime(currentDate, timeZone);
    const cutoffDate = environment.endLotteryDate;
    if (zoneDate >= cutoffDate) {
      this.lotteryEnded.set(true);
    }
  }

  public ngAfterViewInit(): void {
    this._setScrollSubscription();

    if (!environment.production) {
      return;
    }
    this._googleTagManagerService.gtag('event', 'conversion', {
      allow_custom_scripts: true,
      u1: window.location.href,
      u50: '',
      send_to: 'DC-13117613/licz/lote-wys+standard',
    });
  }

  @HostListener('window:scroll', [])
  public onWindowScroll(): void {
    this._scrollSubject.next();
  }

  public onNavigationClick(sectionType: SectionType): void {
    switch (sectionType) {
      case SectionType.RULES:
        this._scroll(this.whatTodoSection);
        break;
      case SectionType.LOTTERY_ENTRY:
        this._scroll(this.formSection);
        break;
      case SectionType.BLIK:
        this._scroll(this.blikSection);
        break;
      case SectionType.PRODUCTS:
        this._scroll(this.productsSection);
        break;
      case SectionType.WINNERS_LIST:
        this._scroll(this.winnersSection);
        break;
      case SectionType.FAQ:
        this._scroll(this.faqSection);
        break;
    }
  }

  public onRegister({
    dto,
    form,
  }: {
    dto: ApplicationSaveDto;
    form: FormGroup<ApplicationFormModel>;
  }): void {
    this._ngxSpinnerService.show().then();
    this._apiService
      .submit(dto)
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        catchError((error) => {
          this._ngxSpinnerService.hide().then();
          if (error === 'timeout') {
            this._toastrService.error(
              'Błąd przekroczenia limitu czasu w trakcie rejestracji. Spróbuj ponownie!',
              'Błąd',
            );
            return throwError(() => error);
          }
          const detail = error.error.detail;
          if ((error.status === 422 || error.status === 400) && detail) {
            if (typeof detail === 'string') {
              this._toastrService.error(detail, 'Błąd');
            } else {
              Object.entries(detail).forEach(([key, value]) => {
                const setErrors = (keyWanted: string, control: FormControl) => {
                  if (key === keyWanted) {
                    const stringError = Object.values(value as object)[0];
                    control.setErrors({
                      backend: stringError,
                    });
                    control.markAllAsTouched();
                    this._changeDetectorRef.markForCheck();
                  }
                };
                setErrors('imie', form.controls.firstname);
                setErrors('nazwisko', form.controls.lastname);
                setErrors('email', form.controls.email);
                setErrors('telefon', form.controls.mobile);
                setErrors('nr_paragonu', form.controls.proofOfPurchaseNumber);
                setErrors('data_paragonu', form.controls.dateOfPurchase);
                setErrors('plik_paragonu', form.controls.imageOfPurchase);
                setErrors('oswiadczenie', form.controls.consentAdult);
                setErrors('regulamin', form.controls.consentRegulations);
                setErrors('zgodadane', form.controls.consentClause);
                setErrors('komunikacja', form.controls.consentCommunication);
                setErrors('klauzula', form.controls.consentExclusion);
                Object.keys(detail[key]).forEach((errorKey) => {
                  this._toastrService.error(detail[key][errorKey], 'Błąd');
                });
                if (!environment.production) {
                  console.warn('form', form);
                }
              });
            }
          } else {
            this._toastrService.error(
              'Wystąpił błąd podczas próby rejestracji. Spróbuj ponownie!',
              'Błąd',
            );
          }
          return throwError(() => error);
        }),
        tap((response) => {
          this._ngxSpinnerService.hide().then();
          form.reset();
          const orderId = uuidv4();
          this._googleTagManagerService.gtag('event', 'purchase', {
            allow_custom_scripts: true,
            value: '1',
            transaction_id: orderId,
            u1: window.location.href,
            u5: orderId,
            u50: '',
            send_to: 'DC-13117613/trans/lote-for+transactions',
          });

          this._router
            .navigate([
              '/zdrapka',
              {
                token: response.token ? response.token : '',
                reward: response.nagroda.tag ? response.nagroda.tag : '',
              },
            ])
            .then();
        }),
      )
      .subscribe();
  }

  private _setScrollSubscription(): void {
    const wasScrolled = [false, false, false, false];
    this._scroll$
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        debounceTime(500),
        tap(() => {
          if (isPlatformServer(this._platformId)) {
            return;
          }
          const scrollDepth = this._getScrollDepth() || 0;
          if (scrollDepth >= 25 && scrollDepth < 50 && !wasScrolled[0]) {
            this._googleTagManagerService.gtag('event', 'conversion', {
              allow_custom_scripts: true,
              u1: window.location.href,
              u4: '25',
              u50: '',
              send_to: 'DC-13117613/licz/lote-scr+standard',
            });
            wasScrolled[0] = true;
          } else if (scrollDepth > 50 && scrollDepth < 75 && !wasScrolled[1]) {
            this._googleTagManagerService.gtag('event', 'conversion', {
              allow_custom_scripts: true,
              u1: window.location.href,
              u4: '50',
              u50: '',
              send_to: 'DC-13117613/licz/lote-scr+standard',
            });
            wasScrolled[1] = true;
          } else if (scrollDepth > 75 && scrollDepth < 100 && !wasScrolled[2]) {
            this._googleTagManagerService.gtag('event', 'conversion', {
              allow_custom_scripts: true,
              u1: window.location.href,
              u4: '75',
              u50: '',
              send_to: 'DC-13117613/licz/lote-scr+standard',
            });
            wasScrolled[2] = true;
          } else if (scrollDepth === 100 && !wasScrolled[3]) {
            this._googleTagManagerService.gtag('event', 'conversion', {
              allow_custom_scripts: true,
              u1: window.location.href,
              u4: '100',
              u50: '',
              send_to: 'DC-13117613/licz/lote-scr+standard',
            });
            wasScrolled[3] = true;
          }
        }),
      )
      .subscribe();
  }

  private _getScrollDepth() {
    if (isPlatformServer(this._platformId)) {
      return;
    }
    const scrollHeight = document.documentElement.scrollHeight;
    const scrollTop = window.scrollY;
    const clientHeight = document.documentElement.clientHeight;
    return Math.round((scrollTop / (scrollHeight - clientHeight)) * 100);
  }

  private _scroll(elementRef: ElementRef | undefined): void {
    if (!elementRef) {
      return;
    }
    elementRef.nativeElement.scrollIntoView({ behavior: 'smooth' });
  }
}
